import React, { useEffect, useRef, useState } from 'react';
import api from '../../lib/api';
import { Layers, Loader2 } from 'lucide-react';

const TFS = ['5m', '15m', '1h', '4h', '1d'];

/**
 * MultiTimeframeSummary — parallel structured analysis across 5 TFs.
 * Compact grid; each row shows trend + momentum + confidence + regime.
 */
export default function MultiTimeframeSummary({ symbol }) {
  const [data, setData] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const ctrlRef = useRef(null);
  const lastRun = useRef(null);

  const run = async () => {
    if (!symbol) return;
    // Cancel any in-flight /mtf call so rapid symbol switches
    // (BTC→SOL→ETH) don't queue up N × 30s LLM calls.
    ctrlRef.current?.abort();
    const ctrl = new AbortController();
    ctrlRef.current = ctrl;
    lastRun.current = symbol;
    setBusy(true); setErr(null);
    try {
      const { data } = await api.post('/crypto/intel/mtf', { symbol, tfs: TFS }, { signal: ctrl.signal });
      if (lastRun.current !== symbol) return; // ignore stale response
      setData(data?.results || {});
    } catch (e) {
      if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
      setErr(e?.response?.data?.detail || 'mtf failed');
    } finally {
      if (ctrlRef.current === ctrl) setBusy(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(run, 500);
    return () => { clearTimeout(t); ctrlRef.current?.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol]);

  const trendColor = (t) => t === 'bullish' ? 'text-buy' : t === 'bearish' ? 'text-sell' : 'text-hold';

  return (
    <div className="border border-[var(--border-default)] bg-[var(--bg-panel)]" data-testid="crypto-mtf">
      <div className="flex items-center justify-between border-b border-[var(--border-default)] px-2 py-1.5">
        <div className="text-[10px] tracking-[0.35em] uppercase text-cyan-sys flex items-center gap-1.5">
          <Layers size={11} /> // mtf summary
        </div>
        <button onClick={run} disabled={busy}
                className="text-[9px] tracking-widest uppercase text-cyan-sys hover:underline flex items-center gap-1 disabled:opacity-40">
          {busy ? <Loader2 size={10} className="animate-spin" /> : null} refresh
        </button>
      </div>
      {err && <div className="p-2 text-[10px] text-sell">// {err}</div>}
      <div className="grid grid-cols-[auto_1fr_1fr_auto] gap-x-2 gap-y-1 text-[10px] font-mono p-2">
        <div className="text-[9px] tracking-[0.35em] uppercase text-[var(--txt-muted)]">TF</div>
        <div className="text-[9px] tracking-[0.35em] uppercase text-[var(--txt-muted)]">Trend</div>
        <div className="text-[9px] tracking-[0.35em] uppercase text-[var(--txt-muted)]">Regime</div>
        <div className="text-[9px] tracking-[0.35em] uppercase text-[var(--txt-muted)] text-right">Conf</div>
        {TFS.map((tf) => {
          const r = data[tf];
          if (!r) return (
            <React.Fragment key={tf}>
              <div className="text-cyan-sys">{tf}</div>
              <div className="text-[var(--txt-muted)]">—</div>
              <div className="text-[var(--txt-muted)]">—</div>
              <div className="text-[var(--txt-muted)] text-right">—</div>
            </React.Fragment>
          );
          if (r.error) return (
            <React.Fragment key={tf}>
              <div className="text-cyan-sys">{tf}</div>
              <div className="col-span-3 text-sell text-[9px] truncate">// {r.error}</div>
            </React.Fragment>
          );
          return (
            <React.Fragment key={tf}>
              <div className="text-cyan-sys" data-testid={`crypto-mtf-tf-${tf}`}>{tf}</div>
              <div className={trendColor(r.trend)}>{r.trend}</div>
              <div className="text-[var(--txt-secondary)]">{r.regime?.replace(/_/g, ' ')}</div>
              <div className={`text-right tabular-nums ${r.confidence >= 75 ? 'text-buy' : r.confidence >= 50 ? 'text-hold' : 'text-sell'}`}>
                {r.confidence}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
